/**
 * GLAMRDiP Email signup modal — opens the dialog on
 * [data-email-signup-trigger] click, submits the customer form via
 * fetch (no full-page redirect), swaps to a success state, and frees
 * focus back to the trigger on close.
 *
 * Native <dialog> element gives us focus-trap + Esc handling for free.
 * The form is rendered with Liquid's {% form 'customer' %} → action
 * `/contact#contact_form` → returns 302/200 depending on redirect
 * config. We always Show success after the request returns ok-or-302
 * (Shopify redirects on success); a real network failure shows a
 * generic error.
 */
(() => {
  'use strict';

  class GDEmailSignup {
    constructor(triggers, modal) {
      this.triggers = [...triggers];
      this.modal    = modal;
      this.form     = modal.querySelector('.gd-email-signup-form');
      this.viewForm    = modal.querySelector('[data-view="form"]');
      this.viewSuccess = modal.querySelector('[data-view="success"]');
      this._lastTrigger = null;
      this._bind();
    }

    open(trigger) {
      this._lastTrigger = trigger || null;
      // Reset to form view in case modal was closed mid-flow.
      if (this.viewForm)    this.viewForm.hidden = false;
      if (this.viewSuccess) this.viewSuccess.hidden = true;
      const submit = this.form?.querySelector('button[type="submit"]');
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.label || submit.textContent;
        submit.dataset.label = submit.dataset.label || submit.textContent;
      }

      const wasOpen = this.modal.open;
      if (typeof this.modal.showModal === 'function') {
        if (!wasOpen) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
      // Shared scroll-lock manager — keeps body locked across chained
      // overlays, restores scroll on final unlock.
      if (!wasOpen) {
        if (window.gdScrollLock) window.gdScrollLock.lock(this.modal);
        else document.body.style.overflow = 'hidden';
      }
      // Focus the email field after the modal opens. Slight delay so the
      // <dialog> autofocus / iOS keyboard behaviour settles.
      setTimeout(() => {
        this.modal.querySelector('input[type="email"]')?.focus();
      }, 80);
    }

    close() {
      const wasOpen = this.modal.open;
      if (typeof this.modal.close === 'function' && wasOpen) {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
      }
      if (wasOpen) {
        if (window.gdScrollLock) window.gdScrollLock.unlock(this.modal);
        else document.body.style.overflow = '';
      }
      this._lastTrigger?.focus?.();
      this._lastTrigger = null;
    }

    _showSuccess() {
      if (this.viewForm)    this.viewForm.hidden = true;
      if (this.viewSuccess) this.viewSuccess.hidden = false;
    }

    _bind() {
      this.triggers.forEach((t) => {
        if (t.dataset.gdEsBound === '1') return;
        t.dataset.gdEsBound = '1';
        t.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(t);
        });
      });

      // Close buttons (× icon + "Continue shopping" inside success view).
      this.modal.querySelectorAll('[data-email-signup-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      // Backdrop click — when the click target is the dialog itself (not
      // its content). Native <dialog> ::backdrop catches outside-clicks.
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });

      // Native dialog 'close' event (fires on Esc) — restore focus + scroll.
      this.modal.addEventListener('close', () => {
        if (window.gdScrollLock) window.gdScrollLock.unlock(this.modal);
        else document.body.style.overflow = '';
        this._lastTrigger?.focus?.();
        this._lastTrigger = null;
      });

      // Form submit via fetch — avoids full-page Shopify redirect.
      if (this.form) {
        const submit = this.form.querySelector('button[type="submit"]');
        if (submit) submit.dataset.label = submit.textContent;
        this.form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!submit) return;
          const orig = submit.dataset.label || submit.textContent;
          submit.textContent = submit.dataset.loadingLabel || 'Signing up...';
          submit.disabled = true;
          try {
            const fd = new FormData(this.form);
            // Shopify's customer form returns a 302 on success. Use
            // redirect: 'follow' but we don't render the response — just
            // treat any non-network-error as success.
            await fetch(this.form.action, {
              method: 'POST',
              body: fd,
              headers: { Accept: 'application/json, text/html' },
              credentials: 'same-origin',
            });
            this._showSuccess();
          } catch (err) {
            submit.textContent = orig;
            submit.disabled = false;
            // Non-blocking error — let the user retry.
            console.warn('[gd-email-signup] submit failed', err);
            alert('Something went wrong. Please try again.');
          }
        });
      }
    }
  }

  function init() {
    // Guard everything so a single failure doesn't break the page (and
    // especially the Shopify theme editor iframe, which surfaces any
    // unhandled exception as "Page failed to load due to an error").
    try {
      const modal = document.querySelector('[data-email-signup-modal]');
      const triggers = document.querySelectorAll('[data-email-signup-trigger]');
      if (!modal || !triggers.length) return;
      if (window.__gdEmailSignup) {
        window.__gdEmailSignup.triggers = [...triggers];
        window.__gdEmailSignup._bind();
        return;
      }
      window.__gdEmailSignup = new GDEmailSignup(triggers, modal);
    } catch (e) {
      console.error('[gd-email-signup] init failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => init());
})();
